import type { TCoachCategory } from "@lexicanon/shared"
import type { MessageDescriptor } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Stack, Text } from "@mantine/core"
import { IconSparkles } from "@tabler/icons-react"
import classes from "../../styles.module.css"
import type { IProps } from "./interfaces"

/** Category → semantic tone, matching the live CoachCard. */
type TTone = "info" | "brand" | "highlight" | "danger" | "warning"
const CATEGORY_TONES: Record<TCoachCategory, TTone> = {
  clarify: "info",
  decide: "brand",
  challenge: "highlight",
  risk: "danger",
  refocus: "warning",
}
const CATEGORY_LABEL: Record<TCoachCategory, MessageDescriptor> = {
  clarify: msg`Clarify`,
  decide: msg`Decide`,
  challenge: msg`Challenge`,
  risk: msg`Risk`,
  refocus: msg`Refocus`,
}

/**
 * CoachQuestionsCard — post-meeting recap of the "Worth asking" questions the
 * facilitator coach raised during the session (persisted live, read back from
 * the meeting). Renders nothing when none were raised.
 */
export function CoachQuestionsCard({ suggestions }: IProps): React.JSX.Element | null {
  const { i18n } = useLingui()
  if (!suggestions || suggestions.length === 0) return null

  return (
    <section className={classes.card}>
      <div className={classes.cardHead}>
        <h2 className={classes.cardTitle}>
          <IconSparkles size={16} color="var(--mantine-color-brand-4)" />
          <Trans>Worth asking</Trans>
        </h2>
        <span className={classes.cardCount}>{suggestions.length}</span>
      </div>
      <Stack gap="sm">
        {suggestions.map((s) => (
          <div key={`${s.timestamp}-${s.question}`}>
            <Badge size="xs" variant="light" color={CATEGORY_TONES[s.category] ?? "info"} mb={4}>
              {i18n._(CATEGORY_LABEL[s.category] ?? CATEGORY_LABEL.clarify)}
            </Badge>
            <Text size="sm" fw={500}>
              {s.question}
            </Text>
            {s.why && (
              <Text size="xs" c="dimmed" fs="italic">
                {s.why}
              </Text>
            )}
          </div>
        ))}
      </Stack>
    </section>
  )
}
